import { CM_PER_KNOT, HANDLING_SLACK_CM, TIE_TWIST_FACTOR } from '../engine/thread';
import { FLOSS } from '../data/dmc';

/**
 * The honesty page. Everything the app is guessing about is written down here
 * in plain words, because the cost of a wrong number falls on the person
 * cutting the thread, not on the person who wrote the code.
 */
export function HelpScreen() {
  return (
    <div className="screen">
      <section className="card">
        <h2>How Knotwright works</h2>
        <p>
          You describe a bracelet. Knotwright turns that into a handful of complete designs, then
          works out the exact knots and thread lengths for the one you pick. It runs entirely in this
          browser — there is no account, no server, and no database. A design is stored inside its own
          link, which is why you can send one to someone and they see exactly what you saw.
        </p>
        <h3>The same words always give the same designs</h3>
        <p>
          Every design comes from your description plus a short <em>seed</em>. Same words, same seed,
          same designs — today, next month, on any phone. Nothing is random at the moment you press
          the button.
        </p>
      </section>

      <section className="card">
        <h2>Where AI is used, and where it is not</h2>
        <p>
          <strong>Used for:</strong> reading your words or your photo and deciding what colours and
          mood you meant. That is a taste question, and a language model is good at it.
        </p>
        <p>
          <strong>Never used for:</strong> knots, rows, charts, string order, or thread lengths. Those
          are arithmetic and simulation. A model that produces a plausible-looking chart with one knot
          wrong costs you an evening and a pile of floss, and it has no way to know it was wrong.
          Knotwright computes all of that with code that has tests, including tests against known-correct
          published patterns.
        </p>
        <p>
          With no API key, the app makes <strong>no network calls at all</strong>. Colours come from a
          word list and, for photos, from reading the pixels on this device. That path is the default,
          not a degraded mode.
        </p>
      </section>

      <section className="card">
        <h2>How the cut lengths are worked out</h2>
        <p>Every string gets its own length, from the same three-part sum:</p>
        <pre className="formula">
{`cut = (bracelet body + ${HANDLING_SLACK_CM} cm handling slack)
    + (knots that string ties × ${CM_PER_KNOT} cm)
    + (2 ends × tie length × ${TIE_TWIST_FACTOR.toFixed(2)} twisting take-up)

then rounded UP to the next 5 cm`}
        </pre>
        <p>
          The middle line is the one that matters. A string that ties a lot of knots eats far more
          thread than one that mostly sits still — in an alpha bracelet the leader thread ties
          hundreds of knots and every base string ties a handful, so cutting them all the same length
          is how people run out halfway through a word.
        </p>
        <p>
          <strong>Where {CM_PER_KNOT} cm per knot comes from.</strong> Two independent routes. One:
          working backwards from a published thread-length rule (a bracelet with a 14 cm body; a string
          that knots in every row wants about 145 cm, one that never knots wants about 45 cm). Two:
          plain geometry — a knot wraps the thread twice around a bundle about 1.5 mm across, which is
          about 0.94 cm. They bracket each other. The larger figure is used deliberately: extra thread
          gets trimmed off, missing thread means unpicking.
        </p>
      </section>

      <section className="card">
        <h2>What Knotwright cannot promise</h2>
        <ul className="honesty">
          <li>
            <strong>Gauge.</strong> Rows per centimetre depends on how hard you pull. Published figures
            range from about 5 to 7 rows/cm and genuinely disagree with each other. Everything about
            finished length rests on this. Knot a test sample, measure it, and put your own number in
            Settings.
          </li>
          <li>
            <strong>Thread lengths are generous estimates, not measurements.</strong> They have been
            checked against a published formula and against knot geometry, but nobody has knotted every
            one of these patterns and measured the offcuts. They err long on purpose.
          </li>
          <li>
            <strong>DMC codes and colours.</strong> The palette is a small curated list of {FLOSS.length}{' '}
            skeins. Screen colours are approximations of dyed cotton and will not match. Check the code
            on the real skein before you buy.
          </li>
          <li>
            <strong>Motif chains.</strong> Cherries, strawberries and hearts are built as the same
            knot sequence as a daisy, with different colour roles and chain lengths. That is how most
            community tutorials describe them, but it is a simplification: some makers use genuinely
            different constructions for fruit.
          </li>
          <li>
            <strong>Finished length of chain bracelets.</strong> Chain sections stretch and compress
            far more than knotted rows. Measure against the wrist after two or three motifs and add or
            drop one.
          </li>
          <li>
            <strong>Working time.</strong> A rough four seconds per knot. Beginners are much slower;
            so is anyone watching television.
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>Reading the charts</h2>
        <p>
          The big picture at the top of a design is the bracelet <em>as worn</em>, lying flat. The
          chart lower down is the bracelet <em>as knotted</em>: rows run down the page in the order you
          tie them, and the coloured blocks along the top are the setup order, left to right.
        </p>
        <p>
          On a word bracelet the letters read along the length, so on the knotting chart they lie on
          their side. Turn the page a quarter turn anti-clockwise (bring the left edge down) and the
          word reads normally.
        </p>
        <ul className="knot-legend">
          <li>
            <span className="glyph">/</span>
            <div>
              <strong>forward knot</strong>
              <p>Left string over right. They swap places. The left colour shows.</p>
            </div>
          </li>
          <li>
            <span className="glyph">\</span>
            <div>
              <strong>backward knot</strong>
              <p>Right string over left. They swap places. The right colour shows.</p>
            </div>
          </li>
          <li>
            <span className="glyph">&gt;</span>
            <div>
              <strong>forward-backward knot</strong>
              <p>Forward then backward with the same string. Nothing swaps. The left colour shows.</p>
            </div>
          </li>
          <li>
            <span className="glyph">&lt;</span>
            <div>
              <strong>backward-forward knot</strong>
              <p>Backward then forward with the same string. Nothing swaps. The right colour shows.</p>
            </div>
          </li>
        </ul>
      </section>

      <section className="card card--tight">
        <a className="link" href="#/">
          ← Back to designing
        </a>
      </section>
    </div>
  );
}
